<template>
    <auth-layout v-if="isPageReady" :image="2" :byword="byword" :progress="progress" :isProgressBarHidden="isAdminInvited">
        <div v-if="isAdminInvited" class="icon__center">
            <common-icon name="success" size="ultra-large"></common-icon>
        </div>
        <common-content-wrapper width="small">
            <common-page-header
                :heading="
                    isAdminInvited
                        ? $t('pages.auth.signup.steps.practice.practice_missing.invite_sent')
                        : $t('pages.auth.signup.steps.practice.heading', { type: getLabel() })
                "
                :content="pageContent"
            ></common-page-header>
            <div v-if="isAdminInvited">
                <common-button
                    type="primary"
                    size="large"
                    :label="$t('pages.auth.signup.steps.practice.practice_missing.back_to_login')"
                    class="back-to-login-btn"
                    @click="$router.push({ name: 'auth.login' })"
                ></common-button>
            </div>
        </common-content-wrapper>
        <common-content-wrapper v-show="!isAdminInvited && !showAlphabetTable" width="small">
            <invite-admin-form
                v-show="showInviteAdminForm"
                @go-to-practice="goToPractice"
                @sent="adminInvited()"
            ></invite-admin-form>

            <practice-form
                v-show="showRegistrationForm"
                :practice="practice"
                :insurancePlans="insurancePlans"
                :facilityServices="facilityServices"
                :states="states"
                @emitEvent="trackChangesToPracticeType($event)"
                @sent="goToNextStep()"
                @toggle-alphabet-table="toggleAlphabetTable"
            ></practice-form>
        </common-content-wrapper>
        <div v-if="showAlphabetTable">
            <div class="insurance-plans__back-button">
                <common-buttons align="left">
                    <div>
                        <common-icon name="arrow-left-blue" size="tiny"></common-icon>
                        <common-button
                            :label="$t('pages.auth.signup.steps.register_physicians.buttons.back')"
                            type="tertiary"
                            size="tiny"
                            @click="toggleAlphabetTable()"
                        ></common-button>
                    </div>
                    <common-button
                        :disabled="isSaveDisabled"
                        :label="$t('common.microcopy.save')"
                        type="primary"
                        @click="saveChanges"
                    ></common-button>
                </common-buttons>
            </div>
            <alphabet-table
                ref="insuranceTable"
                :returnAllData="true"
                :prefillValues="prefillValues"
                :alhpabetObject="alhpabetObject"
                :itemsOnTop="itemsOnTop"
                :customBottomPosition="'100px'"
                @alphabet-selection-save="updateInsurance"
                @alphabet-selection-selection-change="selectionChanged"
            ></alphabet-table>
        </div>
    </auth-layout>
</template>

<script>
    import SignupPageMixin from 'mixins/SignupPageMixin'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AuthLayout from 'components/layouts/AuthLayout'
    import PracticeForm from 'components/forms/AuthSignupPracticeForm'
    import InviteAdminForm from 'components/forms/AuthInviteAdminRegisterPracticeForm'
    import InsurancePlansAPI from 'api/InsurancePlanGroupsAPI'
    import AlphabetTable from 'components/page-elements/alphabet-table/AlphabetTable'
    import StatesAPI from 'api/StatesAPI'
    import UsersAPI from 'api/UsersAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import UserTypeEnum from '@/enums/UserTypeEnum'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [SignupPageMixin, ScreenSizeDetectionMixin, PageWithDataMixin],

        components: {
            AuthLayout,
            PracticeForm,
            InviteAdminForm,
            AlphabetTable,
        },

        data() {
            return {
                insurancePlanGroups: [],
                facilityServices: [],
                states: [],
                practice: this.$auth.getPractice(),
                user: this.$auth.getUser(),
                practiceType: null,
                showInviteAdminForm: false,
                showRegistrationForm: false,
                isAdminInvited: false,
                showAlphabetTable: false,
                alhpabetObject: {},
                itemsOnTop: [],
                prefillValues: [],
                insurancePlans: [],
                isSaveDisabled: true,
            }
        },

        loadData() {
            return {
                states: StatesAPI.getList(),
            }
        },

        computed: {
            byword() {
                return this.practiceType ? this.$t(`pages.auth.signup.steps.practice.byword.${this.practiceType}`) : null
            },

            pageContent() {
                if (this.isAdminInvited) return this.$t('pages.auth.signup.steps.practice.practice_missing.heading')
                return this.showRegistrationForm
                    ? this.$t('pages.auth.signup.steps.practice.description1')
                    : this.$t('pages.auth.signup.steps.practice.description2')
            },
        },

        methods: {
            trackChangesToPracticeType({ name, payload }) {
                if (name !== 'local:beforeChange:practiceType') {
                    return
                }

                this.practiceType = payload.value
            },

            getLabel() {
                return this.practiceType === PracticeTypeEnum.ANCILLARY
                    ? this.$t('common.practice_type.company')
                    : this.$t('common.practice_type.practice')
            },

            goToPractice() {
                this.showInviteAdminForm = false
                this.showRegistrationForm = true
            },

            adminInvited() {
                this.goToNextStep()
                this.isAdminInvited = true
            },

            shouldShowInviteSendPage() {
                this.isAdminInvited = Boolean(this.$route.query.inviteSent)
            },

            getInsurancePlans() {
                InsurancePlansAPI.getListTransformByLetter().then((val) => {
                    this.alhpabetObject = val.alhpabetObject
                    this.itemsOnTop = val.itemsOnTop
                })
            },

            toggleAlphabetTable() {
                this.showAlphabetTable = !this.showAlphabetTable
                this.prefillValues = cloneDeep(this.insurancePlans)
            },

            updateInsurance(value) {
                this.toggleAlphabetTable()
                this.insurancePlans = value
            },

            saveChanges() {
                this.$refs.insuranceTable.saveChanges()
            },

            selectionChanged(payload) {
                this.isSaveDisabled = !payload.length
            },
        },

        mounted() {
            this.shouldShowInviteSendPage()

            this.practiceType = this.user.practiceType
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = 'mobile'

            if (this.$auth.getUser().userRole?.id !== 1) {
                this.showInviteAdminForm = false
                this.showRegistrationForm = true
            } else {
                UsersAPI.checkJoinInvitesEmails(
                    this.$auth.getUser().id,
                    [{ email: this.$auth.getUser().email }],
                    this.practiceType ? this.practiceType : 'physician',
                ).then((response) => {
                    if (
                        response.emailsCheck.registeredInvites &&
                        response.emailsCheck.registeredInvites.length &&
                        response.emailsCheck.registeredInvites[0].inviteType === UserTypeEnum.PRACTICE
                    ) {
                        this.showInviteAdminForm = false
                        this.showRegistrationForm = true
                    } else {
                        this.showInviteAdminForm = true
                        this.showRegistrationForm = false
                    }
                })
            }
        },

        created() {
            this.getInsurancePlans()
        },

        beforeDestroy: function () {
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = ''
        },
    }
</script>

<style lang="scss" scoped>
    .insurance-plans__back-button {
        padding: 30px 0;

        .buttons {
            justify-content: space-between;
            align-items: center;
        }
        .icon {
            margin-top: 10px;
            margin-right: 10px;
        }
    }
    .back-to-login-btn {
        margin-top: 100px;
        display: inline-block;
        width: 100%;
        text-align: center;
    }
</style>
